import { SpringValue } from "react-spring";
import { z } from "zod";

const ZPointSchema = z.object({
  x: z.number().min(0),
  y: z.number().min(0),
});

export const ZClipSegment = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  start: z.number().min(0),
  end: z.number().nullable().optional(),
  label: z.string().optional(),
  type: z.literal("clip"),
});

export const ZPointBoxSegment = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  start: z.number().min(0),
  end: z.number().nullable().optional(),
  label: z.string().optional(),
  coordinates: z.object({
    top_left: ZPointSchema,
    bottom_right: ZPointSchema,
  }),
  type: z.literal("point_box"),
});

export const ZPointSegment = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  start: z.number().min(0),
  end: z.number().nullable().optional(),
  label: z.string().optional(),
  coordinates: ZPointSchema,
  type: z.literal("point"),
});

export const ZPolygonSegment = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  start: z.number().min(0),
  end: z.number().nullable().optional(),
  label: z.string().optional(),
  coordinates: z.array(ZPointSchema).min(3),
  type: z.literal("polygon"),
});

const ZSegmentUnion = z.discriminatedUnion("type", [ZClipSegment, ZPointBoxSegment, ZPointSegment, ZPolygonSegment]);

export const ZSegment = ZSegmentUnion.refine(
  (data) => !data.end || data.end > data.start,
  "Segment 'end' time must be after 'start' time when provided",
);

// Inferred from the pre-refine union so TypeScript retains variant narrowing
// (ZodEffects widens the discriminator to `any`).
export type TClipSegment = z.infer<typeof ZClipSegment>;
export type TPointBoxSegment = z.infer<typeof ZPointBoxSegment>;
export type TPointSegment = z.infer<typeof ZPointSegment>;
export type TPolygonSegment = z.infer<typeof ZPolygonSegment>;
// Spatial segments — the three variants that can live as track waypoints.
export type TSpatialSegment = TPointBoxSegment | TPointSegment | TPolygonSegment;
export type TSegment = z.infer<typeof ZSegmentUnion>;

// Tree shape:
//
//   root  ::= TVideo | TTrack | TCollection | TSegment
//   <track>      atomic — waypoints[] (no children, no nesting)
//   <collection> children[]
//   <video>      segments[] (scrub-bar track)

/**
 * One waypoint on a track's motion trajectory. Internal data — no id, not
 * individually addressable. `time` is the seconds offset at which this
 * waypoint applies.
 */
export type TTrackWaypoint =
  | {
      kind: "point_box";
      time: number;
      coordinates: { top_left: { x: number; y: number }; bottom_right: { x: number; y: number } };
    }
  | { kind: "point"; time: number; coordinates: { x: number; y: number } }
  | { kind: "polygon"; time: number; coordinates: { x: number; y: number }[] };

export type TTrack = {
  id: string;
  label?: string;
  type: "track";
  /** Set when the first waypoint is added; all waypoints share this kind. */
  waypointKind?: "point_box" | "point" | "polygon";
  waypoints: TTrackWaypoint[];
};

/**
 * Children are homogeneous per collection (runtime invariant — all tracks, all
 * clips, etc.; not enforced by zod so transient streaming states don't blow
 * up). The flattener and renderer both rely on this.
 */
export type TCollection = {
  id: string;
  label?: string;
  type: "collection";
  children: (TTrack | TCollection | TSegment)[];
};

export type TVideo = {
  id: string;
  label?: string;
  type: "video";
  segments: TClipSegment[];
};

const ZBoxWaypoint = z.object({
  kind: z.literal("point_box"),
  time: z.number().min(0),
  coordinates: z.object({ top_left: ZPointSchema, bottom_right: ZPointSchema }),
});

const ZPointOnlyWaypoint = z.object({
  kind: z.literal("point"),
  time: z.number().min(0),
  coordinates: ZPointSchema,
});

const ZPolygonWaypoint = z.object({
  kind: z.literal("polygon"),
  time: z.number().min(0),
  coordinates: z.array(ZPointSchema).min(3),
});

const ZTrackWaypoint = z.discriminatedUnion("kind", [ZBoxWaypoint, ZPointOnlyWaypoint, ZPolygonWaypoint]);

export const ZTrack = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  label: z.string().optional(),
  type: z.literal("track"),
  waypointKind: z.enum(["point_box", "point", "polygon"]).optional(),
  waypoints: z.array(ZTrackWaypoint),
});

// `Input` = unknown because `.default()` + `.optional()` diverge from
// TCollection's output shape (id required on output but optional on input).
export const ZCollection: z.ZodType<TCollection, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.object({
    id: z.string().default(() => crypto.randomUUID()),
    label: z.string().optional(),
    type: z.literal("collection"),
    children: z.array(z.union([ZTrack, ZCollection, ZSegmentUnion])),
  }),
);

export const ZVideo = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  label: z.string().optional(),
  type: z.literal("video"),
  segments: z.array(ZClipSegment),
});

export const ZTimelineItem = z.union([ZVideo, ZTrack, ZCollection, ZSegmentUnion]);

export type TTimelineItem = TVideo | TTrack | TCollection | TSegment;

/** Per-variant partial update. `type` is non-optional to disallow variant changes. */
export type TNodeUpdate =
  | (Partial<Omit<TClipSegment, "type">> & { type?: "clip" })
  | (Partial<Omit<TPointBoxSegment, "type">> & { type?: "point_box" })
  | (Partial<Omit<TPointSegment, "type">> & { type?: "point" })
  | (Partial<Omit<TPolygonSegment, "type">> & { type?: "polygon" })
  | (Partial<Omit<TTrack, "type">> & { type?: "track" })
  | (Partial<Omit<TCollection, "type">> & { type?: "collection" })
  | (Partial<Omit<TVideo, "type">> & { type?: "video" });

/** Output of `useTimelineRows`. Depth is unbounded. Discriminated on `kind`. */
type TRenderableRowBase = {
  id: string; // node id — synthetic `__bare__<segmentId>` for bare-segment rows
  label: string;
  depth: number;
  isCollapsed?: boolean;
  y: number;
  height: number;
};

export type TRenderableRow =
  | (TRenderableRowBase & { kind: "collection-header"; data: TCollection })
  | (TRenderableRowBase & { kind: "track"; data: TTrack })
  | (TRenderableRowBase & { kind: "bare-segment"; data: TSegment });

export type TAnimatedStyle = {
  y: SpringValue<number>;
  height: SpringValue<number>;
  opacity: SpringValue<number>;
};
